import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, ChevronDown, Check } from "lucide-react";

/**
 * Company switcher shown in the top nav ONLY when the current user has access to
 * more than one client. Lets the user toggle between "All companies" (aggregated
 * view) and a single company, scoping the whole app to that client until changed.
 */
export default function CompanySwitcher() {
  const { user, userClientIds, activeClientId, setActiveClientId } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || (userClientIds || []).length < 2) return;
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [user, userClientIds]);

  // Hide entirely unless the user belongs to 2+ clients
  if (!user || (userClientIds || []).length < 2) return null;

  const current = activeClientId
    ? (clients.find((c) => c.id === activeClientId)?.name || "…")
    : "All companies";

  const pick = (id) => {
    setActiveClientId(id);
    setOpen(false);
    // trigger any listeners that scope by client
    window.dispatchEvent(new CustomEvent("hbt:active-client-changed", { detail: { id } }));
  };

  return (
    <div className="relative" data-testid="company-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm hover:bg-black/[0.03] transition-colors"
        style={{ borderColor: "var(--border)" }}
        data-testid="company-switcher-btn"
      >
        <Building2 className="w-4 h-4" style={{ color: "var(--forest)" }} />
        <span className="max-w-[180px] truncate">{current}</span>
        <ChevronDown className="w-3 h-3 text-neutral-500" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 hbt-card p-1.5 w-64" data-testid="company-switcher-menu">
            <button
              onClick={() => pick("")}
              className="w-full text-left px-3 py-2 rounded hover:bg-black/[0.04] text-sm flex items-center justify-between"
              data-testid="company-switcher-all"
            >
              <span className="font-medium">All companies</span>
              {!activeClientId && <Check className="w-3.5 h-3.5" style={{ color: "var(--forest)" }} />}
            </button>
            <div className="border-t my-1.5" style={{ borderColor: "var(--border)" }} />
            {clients.map((c) => (
              <button
                key={c.id}
                onClick={() => pick(c.id)}
                className="w-full text-left px-3 py-2 rounded hover:bg-black/[0.04] text-sm flex items-center justify-between"
                data-testid={`company-switcher-opt-${c.id}`}
              >
                <span className="truncate">{c.name}</span>
                {activeClientId === c.id && <Check className="w-3.5 h-3.5" style={{ color: "var(--forest)" }} />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
